/**
 * Fit a Font Awesome glyph to the chip it is drawn in, and centre it on its ink.
 *
 * 🚨 A glyph is not its element, and CSS can see neither.
 *
 * Font Awesome sizes the element by font-size and a fixed `width` of about
 * 1.25em, but the glyph inside is painted at whatever width the font declares,
 * starting at that element's left edge. Ordinary icons are square and centred
 * within their own advance, so the two agree and nobody notices. A Font
 * Awesome Kit logo does neither. Measured on fbsfb.com, in em:
 *
 *   glyph                    ink w x h     advance   note
 *   fa-acc                   3.39 x 0.97    3.40     spilled 17px out of a 34px chip
 *   fa-big-ten-logo          2.65 x 1.01    2.63
 *   fa-pac-12-wordmark       2.48 x 1.00    2.46
 *   fa-sun-belt-...          2.08 x 1.00    2.06
 *   fa-mountain-west         0.76 x 0.40    1.78     half-size, and well off-centre
 *   fa-solid fa-bullhorn     0.96 x 0.98    1.00     an ordinary icon
 *
 * Note `fa-mountain-west`: 0.76em of ink inside a 1.78em advance, and only
 * 0.40em tall. Centring its element, or even its glyph box, still leaves the
 * mark visibly high and off to one side. Only the INK can be centred, and the
 * ink is what a reader sees.
 *
 * 🚨 `getBoundingClientRect()` returns the layout box, not the ink, so every
 * icon on a page measures identically and an overflow scan reports nothing at
 * all. The ink has to be measured on a canvas.
 */

/**
 * Metrics belong to the GLYPH, so they are cached by glyph + family + weight
 * and measured once. Without this the work repeats on every Mithril redraw.
 */
const INK = new Map();
const REF = 100;
let ctx = null;
function measure(before) {
  /*
   * 🚨 FA's `content` is `"\f0a1" / ""` — the glyph plus its alt text.
   * Measuring the whole string measures six characters in a fallback font and
   * reports an ordinary bullhorn as 44px wide. Take the first code point only.
   */
  const raw = before.content;
  if (!raw || raw === 'none') return null;
  const glyph = Array.from(raw.replace(/^["']|["']$/g, ''))[0];
  if (!glyph) return null;
  const key = `${glyph}|${before.fontFamily}|${before.fontWeight}`;
  const hit = INK.get(key);
  if (hit) return hit;
  if (!ctx) ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;
  const spec = `${before.fontWeight} ${REF}px ${before.fontFamily}`;

  /*
   * 🚨 Ask whether the FONT is there before measuring it.
   *
   * Testing for a zero-size result is not enough: with the icon font still in
   * flight the browser measures the FALLBACK, which returns a perfectly
   * ordinary non-zero box. Cached, that made a logo render large on one page
   * load and small on the next, with a refresh as the coin toss.
   */
  const fonts = document.fonts;
  if (fonts && typeof fonts.check === 'function') {
    let ready = false;
    try {
      ready = fonts.check(spec, glyph);
    } catch (e) {
      ready = true;
    }
    if (!ready) return null;
  }
  ctx.font = spec;
  const m = ctx.measureText(glyph);
  const w = m.actualBoundingBoxLeft + m.actualBoundingBoxRight;
  const h = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
  if (!(w > 0 && h > 0)) return null;
  const ink = {
    w: w / REF,
    h: h / REF,
    left: m.actualBoundingBoxLeft / REF,
    right: m.actualBoundingBoxRight / REF,
    asc: m.actualBoundingBoxAscent / REF,
    desc: m.actualBoundingBoxDescent / REF,
    adv: m.width / REF,
    fontAsc: (m.fontBoundingBoxAscent ?? m.actualBoundingBoxAscent) / REF,
    fontDesc: (m.fontBoundingBoxDescent ?? m.actualBoundingBoxDescent) / REF
  };
  INK.set(key, ink);
  return ink;
}

/**
 * Widen a chip to hold its glyph.
 *
 * 🚨 Needed because the glyph is positioned absolutely so CSS can place it,
 * which takes it out of flow — a chip set to `width: auto` then has no content
 * to measure and collapses to its minimum, and the wordmarks spill straight
 * back out. The ink has just been measured, so the width is simply known.
 */
function sizeChip(chip, inkWidth) {
  const cs = window.getComputedStyle(chip);
  const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
  const min = parseFloat(cs.minWidth) || 0;
  chip.style.width = `${Math.max(min, Math.ceil(inkWidth + pad))}px`;
}

/**
 * Fit and centre the icon in every element matching `selector`.
 *
 * The selector names the CHIP — the box the glyph must sit inside. Where the
 * chip contains an `<i>` that is the icon; where the chip IS the icon element
 * (the tags page draws the chip's plate on `.icon` itself) it is used directly,
 * which is why the icon is resolved rather than assumed.
 *
 * `fill` is how much of the chip the ink may occupy; `grow` widens the chip to
 * suit the glyph instead of shrinking the glyph to suit the chip.
 *
 * @returns how many glyphs could not be measured yet
 */
export default function fitIcons(selector, fill = 0.82, grow = false) {
  let chips;
  try {
    chips = document.querySelectorAll(selector);
  } catch (e) {
    return 0;
  }

  // How many glyphs could not be measured, i.e. the font is not there yet.
  let unmeasured = 0;
  chips.forEach(chip => {
    const icon = chip.querySelector('i, .icon') || chip;
    const ink = measure(window.getComputedStyle(icon, '::before'));
    if (!ink) {
      unmeasured++;
      return;
    }
    const cs = window.getComputedStyle(icon);
    const size = parseFloat(cs.fontSize);
    const rect = chip.getBoundingClientRect();
    if (!size || !rect.width) return;
    const availW = rect.width * fill;
    const availH = rect.height * fill;
    const inkW = ink.w * size;
    const inkH = ink.h * size;
    let scale = 1;
    if (grow) {
      // The chip is about to be widened to suit, so only height can shrink it.
      if (inkH > availH) scale = availH / inkH;else if (ink.h < 0.8) scale = Math.min(0.95 * size / inkH, availH / inkH);
    } else if (inkW > availW || inkH > availH) {
      // Too big in either direction: rein it in, keeping the aspect.
      scale = Math.min(availW / inkW, availH / inkH);
    } else if (ink.h < 0.8 && /\bfa-kit\b/.test(icon.className)) {
      /*
       * Too small. Only Kit logos are lifted, and only when the glyph is
       * genuinely short: a wordmark 0.4em tall reads as half-size beside a
       * normal icon. Ordinary icons are left alone — scaling those up would
       * resize every icon on the site to fix a handful of logos.
       */
      scale = Math.min(availW / inkW, 0.95 * size / inkH);
    }

    /*
     * Where the ink sits inside the glyph box, in that box's own coordinates.
     *
     * The box is the glyph's advance and the text origin is its left edge. The
     * baseline sits half the line's leading plus the font ascent below the top.
     */
    const lineH = parseFloat(cs.lineHeight) || size;
    const baseline = (lineH - (ink.fontAsc + ink.fontDesc) * size) / 2 + ink.fontAsc * size;
    const inkCx = (ink.right - ink.left) / 2 * size;
    const inkCy = baseline + (ink.desc - ink.asc) / 2 * size;

    /*
     * 🚨 The CSS pins the box's TOP-LEFT to the middle of the chip and scales
     * about that same corner, so the ink lands at exactly (scale x its offset)
     * from the middle — and the shift that centres it is the negative of that.
     * No percentages, and no pivot to reason about.
     *
     * Every earlier attempt centred a BOX instead: the element, or the glyph's
     * advance. Both leave a mark whose ink is not centred within its advance
     * sitting off to one side, which is most conference wordmarks, and one
     * whose ink is short — Mountain West — sitting high as well.
     */
    /*
     * 🚨 Duotone is scaled but never nudged.
     *
     * A duotone icon paints two layers and canvas measures only the primary
     * one, so its apparent ink centre is the centre of half the drawing —
     * measured 3.7px off on the handshake. The two layers share a size, so the
     * scale is still right; only the centring has to be left to the chip.
     */
    const duotone = /fa-(?:sharp-)?duotone/.test(icon.className);

    // For duotone, centre the glyph BOX instead — that is what it did before
    // any of this, and it looked right.
    const tx = -scale * (duotone ? ink.adv * size / 2 : inkCx);
    const ty = -scale * (duotone ? lineH / 2 : inkCy);
    icon.classList.add('Bespoke-fitIcon');
    icon.style.setProperty('--bespoke-icon-fit', scale.toFixed(4));
    icon.style.setProperty('--bespoke-icon-x', `${tx.toFixed(2)}px`);
    icon.style.setProperty('--bespoke-icon-y', `${ty.toFixed(2)}px`);
    if (grow) sizeChip(chip, inkW * scale);
  });
  return unmeasured;
}

/**
 * Fit now, and keep trying while any glyph is still unmeasurable.
 *
 * 🚨 `document.fonts.ready` is not enough on its own. In a single-page app it
 * resolves once the fonts for the FIRST render are in, which is long before
 * anybody navigates to the page these icons are on — so the retry fired
 * against an empty page and never ran again. A Kit face also arrives lazily,
 * well after that. The signal worth retrying on is "did anything fail to
 * measure", which is what this counts.
 */
export function fitIconsWhenReady(selector, fill, grow) {
  let attempts = 0;
  const run = () => {
    const unmeasured = fitIcons(selector, fill, grow);

    // Give up after roughly two seconds: by then the font is either there or
    // it is not coming, and a timer that never stops is worse than an
    // unstyled icon.
    if (unmeasured > 0 && attempts++ < 14) setTimeout(run, 120 + attempts * 120);
  };
  run();
  const fonts = document.fonts;
  if (fonts) {
    if (typeof fonts.ready?.then === 'function') fonts.ready.then(() => fitIcons(selector, fill, grow));
    if (typeof fonts.addEventListener === 'function') {
      fonts.addEventListener('loadingdone', () => fitIcons(selector, fill, grow));
    }
  }
}
flarum.reg.add('ernestdefoe-bespoke', 'forum/util/fitIcons', fitIcons);